// These are utils shared to parse workspace and report view specs

import {ID} from '@wandb/weave/common/util/id';
import {omit} from 'lodash';

import {
  type OrganizedSettings,
  type PanelBankConfig,
  PanelBankConfigState,
  type PanelBankSectionConfig,
  type PanelBankSettings,
} from '../components/PanelBank/types';
import {getMigratedSettings} from '../components/WorkspaceDrawer/Settings/migrateSettings';
import {getCascadingShowLegend} from '../components/WorkspaceDrawer/Settings/runLinePlots/utils/getCascadingShowLegend';
import {type Settings} from '../components/WorkspaceDrawer/Settings/types';
import {
  type FilterV2Root,
  isFilterV2,
  migrateLegacyFiltersToV2,
} from './filterV2';
import {
  EMPTY_PANEL_BANK_CONFIG,
  EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT,
  getDefaultPanelSectionConfig,
} from './panelbankConfigs';
import {GRID_COLUMN_COUNT} from './panelbankGrid';
import {
  PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
  PANEL_BANK_HIDDEN_SECTION_NAME,
} from './panelbankTypes';
import * as Panels from './panels';
import {
  type Config,
  type LayedOutPanel,
  type LayoutParameters,
  type Panel,
} from './panelTypes';
import {DEFAULT_RUNS_SORT} from './queryts';
import {type Sort, type SortKey} from './queryTypes';
import * as RunFeed from './runfeed';
import * as Section from './section';
import * as SelectionManager from './selectionmanager';
import * as TableCols from './tablecols';

/**
 * Module-level flag set by filtersFromJSON when legacy filters are migrated
 * to v2 format. The workspace thunk resets this before parsing and checks it
 * afterward to decide whether to trigger a save.
 */
let _filterMigrationOccurred = false;

export function resetFilterMigrationFlag(): void {
  _filterMigrationOccurred = false;
}

export function didFilterMigrationOccur(): boolean {
  return _filterMigrationOccurred;
}

export function runSetConfigFromJSON(
  runsetNamePrefix: string,
  json: any,
  i: number
): Section.RunSetConfig | undefined {
  if (json != null) {
    // TODO: Call respective fromJSON methods to validate all these fields.

    // WARNING!!: If you change how this works it will probably reset all grouped run colors everywhere.
    // This is because, for the purposes of run coloring, the runsetID is part of the run name.
    // Consider a migration instead, or decide if resetting group colors isn't a big deal.
    const runsetID = Section.runsetIdFromNameParts(
      runsetNamePrefix,
      `Subsection ${i + 1}`
    );
    const filters = filtersFromJSON(json.filters);
    return {
      ...SelectionManager.fromJSON(json),
      id: json.id || runsetID,
      project: json.project,
      filters,
      runFeed:
        (json.runFeed && TableCols.maybeTruncateTableSettings(json.runFeed)) ||
        RunFeed.EMPTY_CONFIG,
      search: json.search ?? {query: ''},
      searchHistory: json.searchHistory ?? [],
      sort: migratedSort(json.sort),
      name: json.name || `Run set ${i + 1}`,
      enabled: json.enabled != null ? json.enabled : true,
      runNameTruncationType: json.runNameTruncationType,
      baselineRunId: json.baselineRunId,
      pinnedRunIds: json.pinnedRunIds,
    };
  }
  return undefined;
}

function filtersFromJSON(json: any): FilterV2Root {
  if (isFilterV2(json)) {
    return json;
  }

  _filterMigrationOccurred = true;
  return migrateLegacyFiltersToV2(json);
}

const migratedSort = (s: Sort | SortKey | null): Sort => {
  const migratedToMultiSort = migrateToMultipleSortKeys(s) ?? DEFAULT_RUNS_SORT;
  return migrateToRemoveSortKeyWithComma(migratedToMultiSort);
};

function migrateToMultipleSortKeys(s: Sort | SortKey | null): Sort | null {
  if (s == null) {
    return null;
  }
  if ('keys' in s) {
    if (s.keys.length === 0) {
      return DEFAULT_RUNS_SORT;
    }
    return s;
  }
  // we unintentionally store old client-side refs in the view spec
  delete (s as any).ref;
  return {keys: [s]};
}

/**
 * This unblocks users with workspaces that sort on metrics with commas in them.
 * This is a temporary migration to unblock users until a better solution is implemented.
 */
function migrateToRemoveSortKeyWithComma(s: Sort): Sort {
  const filteredKeys = s.keys.filter(k => !k.key.name.includes(','));

  if (filteredKeys.length === 0) {
    return DEFAULT_RUNS_SORT;
  }

  return {keys: filteredKeys};
}

export const migratePanelBankSettingsToPanelSettings = (
  curPanelBankSettings: PanelBankSettings,
  curPanelSettings?: Settings
) => {
  if (!curPanelSettings) {
    // no op if no panel settings
    return {
      panelBankSettings: curPanelBankSettings,
      panelSettings: curPanelSettings,
    };
  }

  const newPanelBankSettings = {...curPanelBankSettings};
  const newPanelSettings = {...curPanelSettings};

  // for now, we'll just write the values onto panelSettings without
  // deleting them from panelBankSettings. once we're confident the
  // migration is going well and things are working smoothly, we can
  // come back and remove the deprecated values from PBS

  if (
    curPanelBankSettings.colorRunNames !== undefined &&
    curPanelSettings.colorRunNames === undefined
  ) {
    newPanelSettings.colorRunNames = curPanelBankSettings.colorRunNames;
  }
  if (curPanelBankSettings.maxRuns && !curPanelSettings.maxRuns) {
    newPanelSettings.maxRuns = curPanelBankSettings.maxRuns;
  }
  if (
    curPanelBankSettings.pointVisualizationMethod &&
    !curPanelSettings.pointVisualizationMethod
  ) {
    newPanelSettings.pointVisualizationMethod =
      curPanelBankSettings.pointVisualizationMethod;
  }
  if (
    curPanelBankSettings?.suppressLegends !== undefined &&
    // @ts-expect-error - suppressLegends is deprecated
    curPanelSettings?.suppressLegends === undefined
  ) {
    newPanelSettings.showLegend = getCascadingShowLegend({
      suppressLegends: curPanelBankSettings.suppressLegends,
      showLegend: curPanelSettings.showLegend,
    });
  }
  if (
    curPanelBankSettings.tooltipNumberOfRuns &&
    !curPanelSettings.tooltipNumberOfRuns
  ) {
    newPanelSettings.tooltipNumberOfRuns =
      curPanelBankSettings.tooltipNumberOfRuns;
  }

  return {
    panelBankSettings: newPanelBankSettings,
    panelSettings: newPanelSettings,
  };
};

// Converts the legacy "Custom Visualizations" (pinned panels) section to a PanelBank section
// Returns the new config
export function upgradeToPanelBank(
  legacyConfig?: Config,
  singlePanelBankSection?: boolean // this is true for reports
): PanelBankConfig | PanelBankSectionConfig {
  const panelBankConfig = EMPTY_PANEL_BANK_CONFIG;
  // If there's a custom viz section in state.panels, convert it to a PanelBank section (in state.panelBankConfig)
  const oldPanels = legacyConfig && legacyConfig.views[0].config;
  if (oldPanels && oldPanels.length > 0) {
    // create an empty section
    const newSection: PanelBankSectionConfig = {
      ...getDefaultPanelSectionConfig(),
      name: PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
      type: 'grid',
    };
    // For each panel in oldPanels, create a new panel, copying the config and layout
    const newPanels = oldPanels.map(p => {
      return {
        __id__: ID(),
        config: {...p.config},
        layout: upgradePanelLayoutV0toV1(p.layout),
        viewType: p.viewType,
      } as LayedOutPanel;
    });
    return singlePanelBankSection
      ? // Only return a single section (PanelBankSectionConfig)
        {...newSection, panels: [...newPanels]}
      : // Return a full PanelBankConfig
        {
          ...panelBankConfig,
          sections: [
            {...newSection, panels: [...newPanels]},
            ...panelBankConfig.sections,
          ],
        };
  }
  return singlePanelBankSection
    ? EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT
    : panelBankConfig;
}

// Panel grid layout conversion from V0 to V1
function upgradePanelLayoutV0toV1(
  panelLayoutV0: LayoutParameters
): LayoutParameters {
  const xScale = GRID_COLUMN_COUNT / getV0GridColumnCount();
  // Panels go from height 150px to 32px, and 150/32 rounds to 5.
  // Doing 32px instead of 30px to maintain multiples of 4.
  // This means panels will become slightly taller.
  const yScale = 5;
  return {
    x: panelLayoutV0.x * xScale,
    y: panelLayoutV0.y * yScale,
    w: panelLayoutV0.w * xScale,
    h: panelLayoutV0.h * yScale,
  };
}

function getV0GridColumnCount() {
  return Panels.PANEL_GRID_WIDTH;
}

// This is run in the loadFinished reducer, for run and multi-run workspaces.
// It returns a full PanelBank.
// Put all migrations to the PanelBankConfig object here.
export const migrateWorkspaceToPanelBank = (
  viewType:
    | 'run-view'
    | 'group-view'
    | 'project-view'
    | 'sweep-view'
    | 'runs'
    | 'runs/draft',
  existingPanelBankConfig?: PanelBankConfig | null,
  legacyConfig?: Config | null
): PanelBankConfig => {
  const pbc = existingPanelBankConfig || EMPTY_PANEL_BANK_CONFIG;

  const panelBankConfig =
    pbc && pbc.state === PanelBankConfigState.Init && legacyConfig != null
      ? (upgradeToPanelBank(legacyConfig) as PanelBankConfig)
      : pbc;

  const sections = panelBankConfig.sections.map(s => {
    // We merge this to migrate the object (i.e., add any new attributes that have been added to panel section config)
    return {
      ...getDefaultPanelSectionConfig(),
      ...panelBankSectionFromJSON(s, viewType),
    };
  });

  // If, for some reason, panel bank doesn't contain hidden panels section,
  // a lot of workspace behavior will be broken. Make sure it exists!
  if (sections[sections.length - 1]?.name !== PANEL_BANK_HIDDEN_SECTION_NAME) {
    sections.push(
      getDefaultPanelSectionConfig({name: PANEL_BANK_HIDDEN_SECTION_NAME})
    );
  }

  return {
    ...panelBankConfig,
    sections,
  };
};

export const panelBankSectionFromJSON = (
  panelBankSectionConfig: PanelBankSectionConfig,
  viewType:
    | 'run-view'
    | 'group-view'
    | 'project-view'
    | 'sweep-view'
    | 'runs'
    | 'runs/draft'
): PanelBankSectionConfig => {
  const isReport = viewType === 'runs' || viewType === 'runs/draft';

  const sectionSettings = migratePanelBankSectionSettings(
    isReport,
    panelBankSectionConfig
  );

  return {
    ...omit(panelBankSectionConfig, 'localPanelSettings', 'type'), // remove deprecated properties
    sectionSettings,
    panels: panelBankSectionConfig.panels.map(p => {
      const panel = Panels.panelFromJSON(p as any);
      return migrateWorkspacePanelGridLayout(isReport, panel);
    }) as any,
  };
};

/**
 * As part of the migration to improve settings structure, we moved the section settings from the localPanelSettings
 * to the sectionSettings field. This function migrates the settings from the localPanelSettings to the sectionSettings
 * field.
 */
const migratePanelBankSectionSettings = (
  isReport: boolean,
  panelBankSectionConfig: PanelBankSectionConfig
): OrganizedSettings | undefined => {
  if (isReport) {
    return panelBankSectionConfig.sectionSettings;
  }

  if (panelBankSectionConfig.sectionSettings) {
    return panelBankSectionConfig.sectionSettings;
  }

  if (panelBankSectionConfig.localPanelSettings) {
    // if deprecated settings exist, migrate them over
    return getMigratedSettings(panelBankSectionConfig.localPanelSettings);
  }

  return undefined;
};

/**
 * In the efforts to improve drag and drop behavior in workspaces, we decided to completely remove the grid layout functionality
 * from workspaces. This functions ensures we remove any fields that are no longer needed from the panel configs
 */
const migrateWorkspacePanelGridLayout = (isReport: boolean, panel: Panel) => {
  if (isReport) {
    return panel;
  }

  return omit(panel, 'layout', 'type');
};
